<template>
  <TableWrapper
    mode="customer"
    :sumCount="count"
    title="לקוחות"
    icon="mdi-account-group-outline"
    :labels="customerLabels"
    :showLabels="shouldShowLabels"
    :showFilterButton="shouldShowFilterButton"
    filterMode="customers"
    :isTablet="isTablet"
    :isLaptop="isLaptop"
    @label-click="handleLabelClick"
  >
    <template #print-icon-header>
      <v-btn
        @click="exportToExcel"
        class="content-save-icon"
        variant="text"
        icon="mdi-printer"
        :loading="exportToExcelLoading"
      />
    </template>
    <div class="customer-table-content" @scroll="handleScroll">
      <div class="table-container">
        <StickyTableHeader
          headerWidth="100%"
          :tableInfoLinesHeader="customerTableInfoLinesHeader"
          :showHeader="showHeader"
          :isMobile="isMobile"
          class="sticky-header"
        />
        <div class="table-body d-flex flex-column ga-3">
          <CustomerTableLine
            v-for="(customer, index) in customersList"
            :key="customer.cust"
            :cust="customer.cust"
            :customer="customer"
            :currentExpandBtn="currentExpandBtn"
            :tableLineBackground="isOdd(index + 1) ? '#ffefdb' : 'transparent'"
            @close-expand="setCurrentExpandBtn"
            @set-current-expand-btn="setCurrentExpandBtn"
          />
        </div>
      </div>
    </div>
  </TableWrapper>
  <v-pagination
    size="small"
    v-model="page"
    class="pagination"
    :active-color="'blue'"
    :length="numberOfPages"
    :total-visible="5"
  ></v-pagination>
</template>

<script>
import CustomerTableLine from "./CustomerTableLine.vue";
import TableWrapper from "./TableWrapper.vue";
import StickyTableHeader from "./StickyTableHeader.vue";
import { mapGetters } from "vuex";
import { isOdd } from "../../utilities";

export default {
  components: { CustomerTableLine, TableWrapper, StickyTableHeader },
  props: {
    customersList: Array,
    count: Number,
    // Props for dynamic labels
    customerInAppCount: {
      type: Number,
      default: 0,
    },
    customersMadeOrdersWeekAgo: {
      type: Number,
      default: 0,
    },
  },
  emits: ["getCustomersPagination"],
  data() {
    return {
      currentExpandBtn: -1,
      page: 1,
      exportToExcelLoading: false,
      showHeader: false,
      customerTableInfoLinesHeader: [
        this.$t(`customers.table.columns.customer`),
        this.$t(`customers.table.columns.address`),
        this.$t(`customers.table.columns.distLineDays`),
        this.$t(`customers.table.columns.agent`),
        this.$t(`customers.table.columns.lastOrder`),
        this.$t(`customers.table.columns.balance`),
        this.$t(`customers.table.columns.showInvoice`),
        this.$t(`customers.table.columns.actions`),
      ],
    };
  },
  watch: {
    page() {
      var query = {
        ...this.$route.query,
        page: this.page,
      };

      this.$router.push({ path: this.$route.path, query: query });
    },
    "$route.query": {
      immediate: true,
      handler() {
        this.page = 1;
      },
    },
    "$route.query.page": {
      immediate: true,
      handler() {
        if (this.$route.query.page) {
          if (this.$route.query.page > this.numberOfPages) {
            this.page = this.numberOfPages;
          } else this.page = Number(this.$route.query.page);
        }
      },
    },
  },

  methods: {
    isOdd(number) {
      return isOdd(number);
    },
    setCurrentExpandBtn(custId = -1) {
      this.currentExpandBtn = custId;
    },
    handleScroll(event) {
      this.showHeader = event.target.scrollTop > 0;
    },
    handleLabelClick(filterKey) {
      const isCurrentlyActive = this.$route.query[filterKey] === "true";
      const query = {
        ...this.$route.query,
        appcustomers: undefined,
        iscustomersMadeOrdersWeekAgoFilter: undefined,
        [filterKey]: isCurrentlyActive ? undefined : "true",
        page: 1,
      };
      this.$router.push({ path: this.$route.path, query });
    },
    async exportToExcel() {
      try {
        this.exportToExcelLoading = true;
        const path = this.$route.path.split("/")[2] || "allcustomers";
        const filterParams = {
          ...this.$route.query,
          [path]: path,
        };

        // Remove page parameter for export (we want all records)
        delete filterParams.page;

        const response = await this.$store.dispatch(
          "Customers/exportCustomersToExcel",
          filterParams
        );

        // Create blob from response
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        // Generate filename with current date
        const date = new Date().toISOString().split("T")[0];
        link.download = `customers_${date}.xlsx`;

        // Trigger download
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error exporting to Excel:", error);
        alert("שגיאה בייצוא הקובץ");
      } finally {
        this.exportToExcelLoading = false;
      }
    },
  },
  computed: {
    numberOfPages() {
      if (this.count === 0) return 1;
      return Math.ceil(this.count / 30);
    },
    customerLabels() {
      const labels = [];
      if (this.customerInAppCount !== undefined) {
        labels.push({
          text: `לקוחות רשומים: ${this.customerInAppCount}`,
          filterKey: "appcustomers",
          isActive: this.$route.query.appcustomers === "true",
        });
      }
      if (this.customersMadeOrdersWeekAgo !== undefined) {
        labels.push({
          text: `לקוחות פעילים: ${this.customersMadeOrdersWeekAgo ?? 0}`,
          filterKey: "iscustomersMadeOrdersWeekAgoFilter",
          isActive:
            this.$route.query.iscustomersMadeOrdersWeekAgoFilter === "true",
        });
      }
      return labels;
    },
    shouldShowLabels() {
      return (
        this.getUserRole?.userroll === "admin" && this.customerLabels.length > 0
      );
    },
    shouldShowFilterButton() {
      return this.isMobile || this.isTablet || this.isLaptop;
    },
    ...mapGetters({
      isMobile: "isMobile",
      isTablet: "isTablet",
      isLaptop: "isLaptop",
      getUserRole: "AuthModule/getUserRole",
    }),
  },
};
</script>

<style scoped lang="scss">
.customer-table-content {
  position: relative;
  height: calc(100vh - 200px);
  overflow-y: auto;
}

.content-save-icon {
  align-self: flex-start;
}

.table-container {
  position: relative;
  width: 96%;
  margin: auto;
}

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: $controlPanelMainColor;
}

.table-body {
  width: 100%;
  padding: 20px 0px;
}
</style>
